// The payment-details-only page (Session ARZ, Item 3; no login). The "Request payment details" email links here, for a
// subcontractor who registered before the form asked for bank details (the live book), or whose details must be given
// again. One-time link: it works once and expires after 7 days.
//
// It shows the company and contact name only (never anything already on file, not even masks), asks for the same four
// fields as the registration form (PaymentDetailsFields, shared), and says the same things: why ELE needs them, that they
// are stored securely, that ELE will phone to confirm before the first payment, and that ELE never asks by email. The
// server re-checks everything and is the authority. House rules: plain English, no double hyphen, the one phone number.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { WhatsAppLink } from "../../components/WhatsAppLink";
import { PrivacyNoticeLink } from "../../components/PrivacyNoticeLink";
import { PaymentDetailsFields, PaymentDetailsIntro } from "./PaymentDetailsFields";
import {
  EMPTY_PAYMENT_DETAILS,
  paymentDetailsErrors,
  type PaymentDetailsField,
  type PaymentDetailsForm,
  type PaymentDetailsRequestPageView,
} from "../../lib/subcontractorPayments";
import "./SubcontractorPages.css";

const PHONE = "0203 576 4760";
const LOGO = "/ELE_logo_new_reqt_FINALv2PNG.png";

export function SubcontractorPaymentDetails() {
  const { token = "" } = useParams();
  const [view, setView] = useState<PaymentDetailsRequestPageView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<"not_found" | "expired" | "error" | null>(null);
  const [form, setForm] = useState<PaymentDetailsForm>(EMPTY_PAYMENT_DETAILS);
  const [errors, setErrors] = useState<Partial<Record<PaymentDetailsField, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.publicPaymentDetails
      .get(token)
      .then((v) => live && setView(v))
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 404) setLoadError("not_found");
        else if (err instanceof ApiError && err.status === 410) setLoadError("expired");
        else setLoadError("error");
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [token]);

  async function onSubmit() {
    if (submitting) return;
    const e = paymentDetailsErrors(form);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setSubmitError("Please correct the highlighted items and try again.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.publicPaymentDetails.submit(token, form);
      setDone(true);
      setForm(EMPTY_PAYMENT_DETAILS);
    } catch (err) {
      const field = err instanceof ApiError ? err.body?.field : undefined;
      if (err instanceof ApiError && err.status === 410) setLoadError("expired");
      else if (err instanceof ApiError && typeof field === "string" && field in EMPTY_PAYMENT_DETAILS) {
        setErrors({ [field as PaymentDetailsField]: err.code });
        setSubmitError("Please check your payment details.");
      } else if (err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")) setSubmitError(err.code);
      else setSubmitError(`Something went wrong sending your payment details. Please try again, or call us on ${PHONE}.`);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Shell>
        <p className="text-center" style={{ color: "var(--sp-ink-muted)" }}>
          Loading...
        </p>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div className="sp-state-box -error">
          <h2>
            {loadError === "not_found" ? "This link is not valid" : loadError === "expired" ? "This link has expired" : "Something went wrong"}
          </h2>
          <p>
            {loadError === "expired"
              ? `This link has expired or has already been used. If you still need to send your payment details, please call East London Elevators on ${PHONE} and we will send a fresh one.`
              : loadError === "not_found"
                ? "Please check the link in your email, or contact East London Elevators and we will help."
                : "Please try again in a moment, or contact East London Elevators and we will help."}
          </p>
          <p className="mt-4 text-sm font-semibold" style={{ color: "var(--sp-blue)" }}>
            Call us on {PHONE}
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="sp-state-box -done">
          <div aria-hidden="true" className="sp-tick">
            {"✓"}
          </div>
          <h2>Thank you</h2>
          <p>
            Your payment details have been sent to East London Elevators. Before we make the first payment to this account,
            we will phone you to confirm them. For any queries call {PHONE}.
          </p>
        </div>
      </Shell>
    );
  }

  if (!view) return null;

  return (
    <Shell>
      <header className="mb-6">
        <p className="sp-eyebrow">Payment details</p>
        <h1 className="sp-title">{view.company}</h1>
        <p className="sp-lead">Please enter the bank account you would like East London Elevators to pay your invoices into.</p>
      </header>

      <section className="sp-sec">
        <h2 className="sp-sec-title">Your bank account</h2>
        <PaymentDetailsIntro />
        <PaymentDetailsFields value={form} onChange={setForm} errors={errors} />
      </section>

      {submitError && (
        <p role="alert" aria-live="assertive" className="sp-error-box">
          {submitError}
        </p>
      )}

      <button type="button" onClick={onSubmit} disabled={submitting} className="sp-btn-primary mt-5 sm:w-auto sm:px-10">
        {submitting ? "Sending..." : "Send payment details"}
      </button>

      {/* Session ASQ (Stream G, Item 5 review, F5): this page collects bank details, so it carries the privacy notice
          link once the notice is published. */}
      <footer className="sp-footer">
        <p>For and on behalf of East London Elevators Ltd.</p>
        <p>East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
        <p>
          <WhatsAppLink />
        </p>
        <PrivacyNoticeLink className="mt-2" color="var(--sp-purple)" />
      </footer>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="subpage">
      <div className="sp-hdr">
        <img src={LOGO} alt="East London Elevators" />
        <div className="sp-hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
        </div>
      </div>
      <div className="sp-wrap">{children}</div>
    </div>
  );
}
